import {
  type ComponentType,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";

import type { RadioGroupV2Props } from "src/components/RadioGroupV2/RadioGroupV2.tsx";
import type { SelectV2Props } from "src/components/SelectV2/SelectV2.tsx";
import type { FieldPath, FieldValues } from "react-hook-form";
import type {
  GridFilterItem,
  GridFilterModel,
  GridRowParams,
} from "@mui/x-data-grid-premium";

import type { DisabledField, HiddenField } from "src/system/module.types.ts";
import type {
  Config,
  MediaAttributes,
} from "src/components/DropZoneInputV2/types";
import type { GridSortModel } from "@mui/x-data-grid/models";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import type { Interpolation } from "@emotion/styled";
import type { Theme } from "@emotion/react";
import type { ResizeValues } from "src/components/TextArea/TextArea.types.ts";
import type { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts/PickersShortcuts";
import type { Dayjs } from "dayjs";
import type { SelectLabelProp, SelectValueProp } from "src/system/system.types";
import type { EditorConfig } from "ckeditor5";

export type TextField<FormValues extends FieldValues = FieldValues> = {
  type: "text";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: string;
  label?: string;
  valueGetter?: (value: any, data: any) => string;
  width?: string | number;
  helperText?: ReactNode;
  placeholder?: string;
  floatingError?: boolean;
  placeholders?: TextTemplatePlaceholder[];
  required?: boolean;
  additionalLabelElems?: ReactNode[];
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
  readOnly?: boolean;
  labelWidth?: number;
};

// export type LabelValueField = {
//   type: "label_value";
//   label: string;
// };

export type SwitchField<FormValues extends FieldValues = FieldValues> = {
  type: "switch";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: boolean;
  width?: string | number;
  label: string;
  valueGetter?: (value: any, data: any) => boolean;
  helperText?: ReactNode;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type NumberField<FormValues extends FieldValues = FieldValues> = {
  type: "number";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: number | string | null;
  label?: string;
  valueGetter?: (value: any, data: any) => number | string | null;
  floatingError?: boolean;
  placeholder?: string;
  helperText?: ReactNode;
  precision?: number;
  endAdornment?: ReactNode;
  width?: string | number;
  controls?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
  additionalLabelElems?: ReactNode[];
};

export type CustomField<FormValues extends FieldValues = FieldValues> = {
  type: "custom";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue: any;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  Component?: ComponentType;
  element?: ReactElement;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type SingleSelectOptions =
  | SelectV2Props<any>["options"]
  | {
      resource: string;
      field: string;
    };

export type SingleSelectField<FormValues extends FieldValues = FieldValues> = {
  type: "singleSelect";
  mapError?: (errors: Record<string, string[]>) => string[];
  allowClearValue?: boolean;
  defaultValue?: any;
  label?: ReactNode;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  helperText?: ReactNode;
  additionalLabelElems?: ReactNode[];
  floatingError?: boolean;
  deps?: string[];
  getData?: ({
    signal,
  }: {
    signal: AbortSignal;
    depsValues: any[];
  }) => Promise<{ value: string; label: string }[]>;
  serverOptions?: SingleSelectServerOptions;
  options?: SingleSelectOptions;
  onChange?: (value: any, form: any, resource: any) => void;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type SelectField<FormValues extends FieldValues = FieldValues> = {
  type: "select";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: any;
  label?: string;
  allowClearValue?: boolean;
  resourceKey?: string;
  getResourcesForInitialOptions?: (mainResource: any) => any;
  getInitialOptionsFromMainResource?: boolean;
  valueGetter?: (value: any, data: any) => any;
  helperText?: ReactNode;
  isMultiple?: boolean;
  width?: string | number;
  serverOptions?: SelectServerOptions;
  options?: SelectV2Props<any>["options"];
  floatingError?: boolean;
  onChange?: (value: string, resource: any, form: any) => void;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
  showAdvancedSearch?: boolean;
  optionsMinWidth?: number;
  isButton?: boolean;
  placeholder?: string;
};

export type MultiSelectField<FormValues extends FieldValues = FieldValues> = {
  type: "multiSelect";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: any[];
  label?: string;
  showSearch?: boolean;
  valueGetter?: (value: any, data: any) => string[];
  width?: string | number;
  helperText?: ReactNode;
  serverOptions?: SelectServerOptions;
  floatingError?: boolean;
  options?: SelectV2Props["options"];
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type CheckboxField<FormValues extends FieldValues = FieldValues> = {
  type: "checkbox";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: boolean;
  label?: string;
  valueGetter?: (value: any, data: any) => boolean;
  onChange?: (value: boolean, form: any, name: string) => void;
  width?: string | number;
  floatingError?: boolean;
  helperText?: ReactNode;
  additionalElements?: ReactNode[];
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type ImageField<FormValues extends FieldValues = FieldValues> = {
  type: "image";
  mapError?: (errors: Record<string, string[]>) => string[];
  label?: string;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  helperText?: ReactNode;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type ImageFieldV2<FormValues extends FieldValues = FieldValues> = {
  type: "imageV2";
  mapError?: (errors: Record<string, string[]>) => string[];
  label?: string;
  valueGetter?: (value: any, data: any) => { [uuid: string]: MediaAttributes };
  helperText?: ReactNode;
  width?: string | number;
  hidden?: HiddenField<FormValues>;
  validationRules?: Partial<Config["validationRules"]>;
  multiple?: boolean;
  maxItems?: number;
  disabled?: DisabledField<FormValues> | boolean;
};

export type RadioFieldOptions =
  | RadioGroupV2Props["options"]
  | {
      resource: string;
      field: string;
    };

export type RadioField<FormValues extends FieldValues = FieldValues> = {
  type: "radio";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue: string | boolean | number | null;
  label?: string;
  orientation?: "vertical" | "horizontal";
  valueGetter?: (value: any, data: any) => string | boolean | number;
  width?: string | number;
  options: RadioFieldOptions;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type CkeditorField<FormValues extends FieldValues = FieldValues> = {
  type: "ckeditor";
  mapError?: (errors: Record<string, string[]>) => string[];
  defaultValue?: string;
  height?: CSSProperties["height"];
  placeholders?: TextTemplatePlaceholder[];
  label?: ReactNode;
  valueGetter?: (value: any, data: any) => string;
  width?: string | number;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
  config?: EditorConfig;
};

export type AutocompleteField<FormValues extends FieldValues = FieldValues> = {
  type: "autocomplete";
  mapError?: (errors: Record<string, string[]>) => string[];
  label: string;
  valueGetter?: (value: any, data: any) => string;
  searchFieldName: string;
  floatingError?: boolean;
  width?: string | number;
  filterField: string;
  filterOperator?: string;
  placeholder?: string;
  valueProp?: SelectValueProp<any>;
  labelProp?: SelectLabelProp<any>;

  advancedSearch?: {
    title?: string;
    onRowClick?: (params: { form: any; params: GridRowParams<any> }) => void;
    labelProp?: string;
  };

  startSearchWith?: number;
  onValueChange?: (params: { value: string; item?: any; form: any }) => void;

  resource: string;
  include?: string[];

  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type TextAreaField<FormValues extends FieldValues = FieldValues> = {
  type: "textArea";
  mapError?: (errors: Record<string, string[]>) => string[];
  label?: string;
  valueGetter?: (value: any, data: any) => string;
  helperText?: ReactNode;
  resize?: ResizeValues;
  placeholders?: TextTemplatePlaceholder[];
  width?: CSSProperties["width"];
  height?: CSSProperties["height"];
  floatingError?: boolean;
  placeholder?: string;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type DateField<FormValues extends FieldValues = FieldValues> = {
  type: "date";
  mode?: "dateTime" | "date";
  mapError?: (errors: Record<string, string[]>) => string[];
  label: string;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  floatingError?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type InputRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "inputRange";
  mapError?: (errors: Record<string, string[]>) => string[];
  label: string;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  floatingError?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;

  nameFrom: keyof FormValues & string;
  nameTo: keyof FormValues & string;
  additionalField?: keyof FormValues & string;
  shortcuts?: InputRangeShortcut[];
};

export type GroupField<FormValues extends FieldValues = FieldValues> = {
  type: "group";
  mapError?: (errors: Record<string, string[]>) => string[];
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  floatingError?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;

  title: string;
  fields_order: readonly (
    | (keyof FormValues & string)
    | readonly (keyof FormValues & string)[]
  )[];
};

export type TimeField<FormValues extends FieldValues = FieldValues> = {
  type: "time";
  mapError?: (errors: Record<string, string[]>) => string[];
  label: string;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  floatingError?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type TimeRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "timeRange";
  mapError?: (errors: Record<string, string[]>) => string[];
  label: string;
  valueGetter?: (value: any, data: any) => any;
  width?: string | number;
  floatingError?: boolean;
  hidden?: HiddenField<FormValues>;
  disabled?: DisabledField<FormValues> | boolean;
};

export type InputRangeShortcut = {
  label?: ReactNode;
  getValue: () => [number | null, number | null];
  id: string;
};

export type DateRangeField<FormValues extends FieldValues = FieldValues> = {
  type: "dateRange";
  mapError?: (errors: Record<string, string[]>) => string[];
  label?: string;
  shortcuts?: PickersShortcutsItem<
    [string | null | Dayjs, string | null | Dayjs]
  >[];
  valueGetter?: (value: any, data: any) => [string | null, string | null];
  width?: string | number;
  defaultValue?: [string | null, string | null];
  hidden?: HiddenField<FormValues>;
  floatingError?: boolean;
  disabled?: DisabledField<FormValues> | boolean;
};

export type ValidFormField<FormValues extends FieldValues = FieldValues> =
  | TextField<FormValues>
  | NumberField<FormValues>
  | SingleSelectField<FormValues>
  | CheckboxField<FormValues>
  | MultiSelectField<FormValues>
  | SelectField<FormValues>
  | CustomField<FormValues>
  | DateRangeField<FormValues>
  | RadioField<FormValues>
  | ImageField<FormValues>
  | ImageFieldV2<FormValues>
  | CkeditorField<FormValues>
  | AutocompleteField<FormValues>
  | SwitchField<FormValues>
  | TextAreaField<FormValues>
  | DateField<FormValues>
  | TimeField<FormValues>
  | TimeRangeField<FormValues>
  | InputRangeField<FormValues>
  | GroupField<FormValues>;

export type SelectSearchField = string | string[] | GridFilterItem[];

export type SelectServerOptions = {
  resource: string;
  filter?:
    | GridFilterModel["items"]
    | { (values: any): GridFilterModel["items"] };
  logicOperator?: GridFilterModel["logicOperator"];
  sort?: GridSortModel;
  labelProp?: SelectLabelProp<any>;
  withoutDescendants?: boolean;
  valueProp?: string;
  include?: string[];
  searchField?: SelectSearchField | { (search: string): SelectSearchField };
  optionMetaField?: string;
};

export type SingleSelectServerOptions = {
  resource: string;
  filter?:
    | GridFilterModel["items"]
    | { (values: any): GridFilterModel["items"] };
  logicOperator?: GridFilterModel["logicOperator"];
  sort?: GridSortModel;
  labelProp?: SelectLabelProp<any>;
  valueProp?: SelectValueProp<any>;
  include?: string[];
};

export type ValidFormFields = Record<string, ValidFormField>;

export enum FormAction {
  Edit = "edit",
  Create = "create",
}

export type ValidArrayField<FormValues extends FieldValues = FieldValues> =
  | (keyof FormValues & string)
  | "";

export type FieldName<FormValues extends FieldValues = FieldValues> =
  | (keyof FormValues & string)
  | readonly ValidArrayField[];

export type FieldsOrder<FormValues extends FieldValues = FieldValues> =
  readonly FieldName<FormValues>[];

export type CustomFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "custom";
  Component: ComponentType;
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type PriceFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "price";
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type DefaultFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "default";
  fields_order: FieldsOrder<FormValues>;
  title?: string;
  helperText?: string;
  css?: Interpolation<Theme>;
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type CommentsFormPanelV2<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "comments-v2";
  resource?: string;
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type CommentsFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "comments";

  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
  notes: {
    id: string;
    content: string;
    author: string;
    created_at: string;
  }[];
  onCreateComment: (params: { content: string }) => Promise<void>;
};

export type HiddenPanelConfig<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> =
  | {
      watch_names?: readonly [...FieldNames];
      hide_condition: (values: FormValues) => boolean;
    }
  | { (values: FormValues): boolean };

export type ContactsFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "contacts";

  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
};

export type TransferPanelV2<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "transfer-v2";
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;
  mainResource?: string;
  transferResource: string;
};

export type TransferPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> = {
  type: "transfer";
  hidden?: HiddenPanelConfig<FormValues, FieldNames>;

  formResource: string;
  resource: string;
};

export type ValidFormPanel<
  FormValues extends FieldValues = FieldValues,
  FieldNames extends
    readonly FieldPath<FormValues>[] = readonly FieldPath<FormValues>[],
> =
  | CustomFormPanel<FormValues, FieldNames>
  | DefaultFormPanel<FormValues, FieldNames>
  | TransferPanel<FormValues, FieldNames>
  | TransferPanelV2<FormValues, FieldNames>
  | CommentsFormPanel<FormValues, FieldNames>
  | ContactsFormPanel<FormValues, FieldNames>
  | CommentsFormPanelV2<FormValues, FieldNames>
  | PriceFormPanel<FormValues, FieldNames>;
